import type { LoroDoc } from "loro-crdt";
import { useTranslation } from "react-i18next";
import { rotationOf } from "../core/circle";
import { setProp } from "../core/ops";
import {
  MAX_PER_ROUND,
  PER_ROUND,
  RANDOM_PEOPLE,
  RANDOM_SUBTASKS,
  perRound,
  type Fairness,
  type Round,
} from "../core/parts";
import type { Task } from "../core/schema";
import { Checkbox, Why } from "../ui";
import { NumberInput } from "../ui/NumberInput";

/**
 * The settings of rotating subtasks (ADR 0035), under the subtasks: where people take turns, how many of them have a
 * part each round, and whether people and subtasks are drawn at random. Fair either way, and the *Why?* says how.
 */
export function SubtaskRound({
  task,
  round,
  fair,
  shared,
  memberIds,
  ro,
  write,
}: {
  task: Task;
  round: Round | null;
  /** the workspace's *Random picks* (ADR 0037) */
  fair: Fairness;
  shared: boolean;
  memberIds: string[];
  ro: boolean;
  write(fn: (doc: LoroDoc) => void): void;
}) {
  const { t } = useTranslation();
  const people = shared ? rotationOf(task, memberIds) : [];
  const randomPeople = task.props[RANDOM_PEOPLE] === true;
  const randomSubtasks = task.props[RANDOM_SUBTASKS] === true;
  const chanceP = people.length > 1 && randomPeople && fair.people === "chance";
  const chanceS = randomSubtasks && fair.subtasks === "chance";
  const set = (prop: string, v: boolean) => write((doc) => setProp(doc, task.id, prop, v ? true : null));
  const label = "flex w-fit items-center gap-2 text-[13px]";
  return (
    <div className="mt-2 flex flex-col gap-1.5" data-testid="subtask-round">
      {people.length > 1 && (
        <label className={label}>
          <span>{t("parts.perRound")}</span>
          <NumberInput
            value={perRound(task)}
            min={1}
            max={Math.min(MAX_PER_ROUND, people.length)}
            disabled={ro}
            onCommit={(n) => write((doc) => setProp(doc, task.id, PER_ROUND, n === 1 ? null : n))}
            className="kss-control kss-field h-[30px] w-16 px-2 text-[13px]"
            data-testid="parts-per-round"
          />
        </label>
      )}
      {people.length > 1 && (
        <label className={label}>
          <Checkbox
            checked={randomPeople}
            disabled={ro}
            label={t("parts.randomPeople")}
            onChange={(v) => set(RANDOM_PEOPLE, v)}
          />
          {t("parts.randomPeople")}
        </label>
      )}
      <label className={label}>
        <Checkbox
          checked={randomSubtasks}
          disabled={ro}
          label={t("parts.randomSubtasks")}
          onChange={(v) => set(RANDOM_SUBTASKS, v)}
        />
        {t("parts.randomSubtasks")}
      </label>
      <p className="flex items-center gap-1 text-[12px] text-neutral-500">
        {chanceP || chanceS
          ? t("parts.chanceHint")
          : people.length > 1
            ? t("parts.fairHint")
            : t("parts.rotateHint")}
        <Why
          title={t("parts.thisRound")}
          formula={[
            people.length > 1 &&
              (chanceP
                ? t("parts.whyPeopleChance")
                : t("parts.whyPeople", {
                    how: randomPeople ? t("parts.atRandom") : t("parts.inOrderWord"),
                  })),
            chanceS
              ? t("parts.whySubsChance")
              : t("parts.whySubs", {
                  how: randomSubtasks ? t("parts.atRandom") : t("parts.inOrderWord"),
                }),
            t("parts.whySame"),
          ]
            .filter(Boolean)
            .join(" ")}
          rows={[
            [t("parts.thisRound"), String((round?.round ?? 0) + 1)],
            ...(people.length > 1
              ? [[t("parts.perRound"), String(round?.parts.length ?? 1)] as [string, string]]
              : []),
          ]}
        />
      </p>
    </div>
  );
}
